import Title, { TitleProps } from '@/components/content/common/Title'
import ScrollView from '@/components/ui/ScrollView'
import AppIcon from '@/components/content/app/AppIcon'
import { Icon } from '@/icons/Icon'
import { getBrandColors } from '@/lib/utils'
import { createLogger } from '@/lib/logger'
import type {
  Alternative,
  App,
  AppCategoryRecommendation,
  Category,
  Stack,
  StackAppsApp,
} from '@/payload-types'

const logger = createLogger('Categories')

type CategoryFilter = Pick<Category, 'id' | 'name' | 'slug' | 'icon' | 'appRecommendations'> & {
  comparisonCount?: number
}

type CategoriesProps = {
  header?: TitleProps
  categories: CategoryFilter[]
  collection: string
  handleSelected: (selection: CategoryFilter) => void
}

export default function Categories({
  header,
  categories,
  collection,
  handleSelected,
}: CategoriesProps) {
  return (
    <div className="col-span-1 -ml-3 flex w-[calc(100%+24px)] flex-col gap-5 md:col-span-2 md:ml-0 md:w-full md:gap-12">
      {header && (
        <div className="px-3 md:px-0">
          <Title {...header} />
        </div>
      )}
      <ScrollView className="flex gap-9 px-5 md:px-0">
        {categories.slice(0, 6).map((category, index) => {
          const totalApps = category.appRecommendations?.docs?.length || 0
          const docs = category.appRecommendations?.docs as AppCategoryRecommendation[] | undefined

          const primaryApp = (docs?.find((acr) => acr.classification === 'primary')?.app ||
            docs?.find((acr) => acr.classification === 'secondary')?.app ||
            docs?.[0]?.app) as App | undefined

          const { backgroundColor } = getBrandColors(primaryApp)

          const totalReviews = Array.isArray(docs)
            ? docs.reduce((total: number, acr) => {
                const reviewCount = (acr.app as App)?.review?.docs?.length || 0
                return total + reviewCount
              }, 0)
            : 0

          const totalDeals = Array.isArray(docs)
            ? docs.reduce((total: number, acr) => {
                const dealCount = (acr.app as App)?.deals?.docs?.length || 0
                return total + dealCount
              }, 0)
            : 0

          const totalAlternatives = Array.isArray(docs)
            ? docs.reduce((total: number, acr) => {
                const altCount =
                  ((acr.app as App)?.alternatives?.docs as Alternative[])?.length || 0
                return total + altCount
              }, 0)
            : 0

          const acrs = category.appRecommendations?.docs as AppCategoryRecommendation[]
          const totalStacks =
            collection === 'stacks' && Array.isArray(acrs)
              ? acrs.reduce((uniqueStackIdSet: Set<Stack['id']>, acr) => {
                  const app = (acr as AppCategoryRecommendation)?.app as App | undefined

                  const stackApps = app?.stackApps?.docs as StackAppsApp[] | undefined

                  if (!stackApps || !Array.isArray(stackApps)) {
                    logger.error('Stack apps not populated')
                    return uniqueStackIdSet
                  }

                  stackApps.forEach((stackApp) => {
                    const stack = stackApp.stack

                    uniqueStackIdSet.add(typeof stack === 'number' ? stack : (stack as Stack).id)
                  })

                  return uniqueStackIdSet
                }, new Set<Stack['id']>()).size
              : 0
          return (
            <div
              key={index}
              onClick={() => handleSelected(category)}
              className="group flex cursor-pointer flex-col items-center gap-3 text-center"
            >
              <div className="flex h-36 w-36 shrink-0 items-center justify-center overflow-hidden rounded-full bg-gray-100 text-gray-500 shadow-icon transition-transform duration-300 group-hover:scale-95 dark:bg-white/5 dark:text-white/60">
                {primaryApp && (primaryApp.glyph || primaryApp.icon) ? (
                  <AppIcon
                    icon={primaryApp.icon}
                    glyph={primaryApp.glyph}
                    name={primaryApp.name}
                    background={backgroundColor}
                    circular={true}
                    size={144}
                  />
                ) : category.icon ? (
                  <Icon name={category.icon} size={40} strokeWidth={2.5} />
                ) : null}
              </div>
              <div className="flex flex-col gap-1">
                <h3 className="leading-tight">{category.name}</h3>
                <p className="text-12">
                  <span>{`${totalApps} app${totalApps === 1 ? '' : 's'}`}</span>
                  <span>, </span>

                  {collection === 'stacks' ? (
                    <span>{`${totalStacks} stack${totalStacks === 1 ? '' : 's'}`}</span>
                  ) : collection === 'deals' ? (
                    <span>{`${totalDeals} deal${totalDeals === 1 ? '' : 's'}`}</span>
                  ) : collection === 'comparisons' ? (
                    <span className="text-gray-400">{`${category.comparisonCount ?? 0} comparison${category.comparisonCount === 1 ? '' : 's'}`}</span>
                  ) : collection === 'alternatives' ? (
                    <span className="text-gray-400">{`${totalAlternatives} alternative${totalAlternatives === 1 ? '' : 's'}`}</span>
                  ) : (
                    <span className="text-gray-400">{`${totalReviews} ${collection}`}</span>
                  )}
                </p>
              </div>
            </div>
          )
        })}
      </ScrollView>
    </div>
  )
}
